import { z } from 'zod/v4';

import { subMenuItemSchemaLegacy } from '@sites/entity-core';

/**
 * Shared by `create/update_navigation_widget` and `create/update_menu_widget` — both widgets carry
 * a `menuList` of this exact shape (the host app duplicates the TS type per widget; this package
 * shares the one AI-facing schema instead, same reasoning as `partialButtonItemSchema`).
 *
 * Field names match the entity 1:1 — no renaming fold needed here, unlike most widget schemas.
 * `linkUrl` is the real field. The host app's own "+ add menu item" button seeds a bogus `value`
 * field instead (`menu-control.component.ts:242`, `navigation-control.component.ts:131` — a live
 * bug, not a naming choice); do not describe or default to `value`.
 *
 * `id` is never exposed: it is server-generated (cuid2, mirroring `menu.model.ts`'s
 * `dataAdapter`), so the mutation layer backfills it for any item that arrives without one — a
 * model has no legitimate value to put there.
 *
 * All fields optional (partial), like `partialButtonItemSchema`: `menuList` is replaced WHOLESALE
 * on both create and update (no per-item merge with a prior array — there is no stable key to
 * merge by once `id` is hidden from the model), so each item the fold writes needs sensible
 * defaults for anything omitted rather than a "current value" to fall back to.
 */
const partialSubMenuItemSchema = z.object({
    name: z.string().optional().describe('Text shown for this menu entry.'),
    linkUrl: z
        .string()
        .optional()
        .describe(
            'Where it links to: an absolute https:// URL, a phone number (linkType "phone"), or a mailto ' +
                'address (linkType "email"). For linkType "anchor", the target block\'s id (from get_page_snapshot).'
        ),
    linkType: z
        .enum(['email', 'phone', 'link', 'anchor'])
        .optional()
        .describe('"link" (URL), "email", "phone", or "anchor" (jumps to a block on this page).'),
    isVisible: z.boolean().optional().describe('Whether this entry renders at all. Defaults to true.'),
    isTargetBlank: z.boolean().optional().describe('Open in a new tab. Only meaningful for linkType "link".'),
    pageId: z.string().optional().describe('Target page id, for a link to another page of this site.'),
    blockId: z.string().optional().describe('Target block id, for linkType "anchor".'),
});

export const partialMenuItemSchema = partialSubMenuItemSchema.extend({
    submenuList: z
        .array(partialSubMenuItemSchema)
        .optional()
        .describe('Dropdown entries shown under this one. One level deep only — a submenu entry cannot nest another.'),
});

export type PartialMenuItem = z.infer<typeof partialMenuItemSchema>;
export type PartialSubMenuItem = z.infer<typeof partialSubMenuItemSchema>;

/** Default values a menu-item fold applies to whatever the model omitted — never partially merged. */
export const MENU_ITEM_DEFAULTS: Required<Omit<PartialSubMenuItem, 'pageId' | 'blockId'>> = {
    name: 'Link',
    linkUrl: 'https://example.com',
    linkType: 'link',
    isVisible: true,
    isTargetBlank: false,
};

/**
 * `EffectTypes` in the host app
 * (`apps/websites/src/app/shared/components/web-hover-effects/types/index.ts`) is a NUMERIC enum
 * — `transparency=0, color=1, underline=2, animation=3` — because that is what gets persisted. A
 * model has no reason to know these positions, so the AI surface exposes the names and this map
 * folds a name to its index; `hoverEffectTypeFromIndex` is its inverse, for reading a stored value
 * back out when a widget is patched (mirrors `entity-core`'s own `hoverEffectsSchemaLegacy`, which
 * keeps the numeric form since that is what real data parses against).
 */
export const HOVER_EFFECT_TYPE_TO_INDEX: Readonly<Record<string, number>> = {
    transparency: 0,
    color: 1,
    underline: 2,
    animation: 3,
};

const HOVER_EFFECT_TYPE_NAMES = ['transparency', 'color', 'underline', 'animation'] as const;

export function hoverEffectTypeFromIndex(index: number): (typeof HOVER_EFFECT_TYPE_NAMES)[number] | undefined {
    return HOVER_EFFECT_TYPE_NAMES[index];
}

/**
 * Partial hover-effect settings, shared by `navigation`/`menu`'s `hoverEffects` field. Merged onto
 * the widget's current group on both create and update (like button's `animation`), not replaced
 * wholesale — see `mergeHoverEffectsGroup` in `patch-helpers.ts`.
 */
export const partialHoverEffectsSchema = z.object({
    type: z
        .enum(HOVER_EFFECT_TYPE_NAMES)
        .optional()
        .describe(
            'What happens on hover: "transparency" (opacity change), "color" (text/background swap), ' +
                '"underline" (a line grows in), or "animation" (a canned motion effect).'
        ),
    color: z.string().optional().describe('CSS color used by "color" and "underline".'),
    transparency: z.number().min(0).max(100).optional().describe('Opacity percentage, used by "transparency".'),
    lineType: z.string().optional().describe('Line style, used by "underline" (e.g. "solid").'),
    lineWidth: z.number().optional().describe('Line thickness in pixels, used by "underline".'),
    animationType: z.string().optional().describe('Which canned motion effect, used by "animation".'),
});

export type PartialHoverEffects = z.infer<typeof partialHoverEffectsSchema>;
